<template>
  <div class="component-layout">
    <div class="bg-white shadow-md rounded-md overflow-hidden">
      <div class="border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <h2 class="text-lg font-semibold text-gray-900">{{ getPageTitle }}</h2>
      </div>

      <div class="bg-white rounded-md w-full">
        <customTable :data="myData" :columns="tableColumns" v-bind="tableProps" @row-click="handleRowClick" @search="handleSearch" @sort="handleSort" @filter="handleFilter" @page-change="handlePageChange" :isLoading="isInitialLoading || isLoading">
          <template #table-actions>
            <CustomButton icon="add" iconPosition="right" @click="handleNewContentClick" :button-text="t('entity.newContentButton')" size="sm"/>
          </template>

          <template #title="{ item }">
            <Tooltip :title="item.title" :display-condition="titleTooltipVisible" justify-items="start">
              <template #content>
                <span @mouseover="checkIfTextOverflows" class="text-sm">{{ item.title }}</span>
              </template>
            </Tooltip>
          </template>

          <template #status="{ item }">
            <div class="flex items-center justify-center h-full text-gray-500">
              <span class="material-symbols-outlined !text-[12px]">radio_button_checked</span>
              <p class="ml-2 text-xs text-center ">{{ getEntityStatus(item.entityTypeId) }}</p>
            </div>
          </template>

          <template #publicationStatus="{ item }">
            <div class="flex items-center justify-center h-full" :class="getPublicationStatusColor(item.statusId)">
              <span class="material-symbols-outlined !text-[18px]">{{ getPublicationStatusIcon(item.statusId) }}</span>
              <p class="ml-2 text-xs text-center">{{ getPublicationStatus(item.statusId) }}</p>
            </div>
          </template>

          <template #date="{ item }">
            <div class="flex justify-center text-center">
              <span class="material-symbols-outlined !text-[16px]">calendar_month</span>
              <p class="w-36 text-xs">
                {{ formatDate(item) }} </p>
            </div>
          </template>

          <template #actions="{ item }">
            <div class="flex items-center justify-center">
              <span class="material-symbols-outlined cursor-pointer !text-[18px] text-secondary" @click.stop="handleRowClick(item)">visibility</span>
            </div>
          </template>
        </customTable>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import axios from '@/plugins/axiosInstance'
import { entity } from '@/networking/urlManager'
import { dateHelper } from '@/utils/dateHelper'
import { responseHandlerService } from '@/plugins/responseHandlerService'
import { useAssetStore } from '@/stores/asset'
import { useContentStore } from '@/stores/content'
import customTable from '@/components/table/VTable.vue'
import CustomButton from '@/components/base/buttons/VButton.vue'
import Tooltip from '@/components/common/VTooltip.vue'


// Enums
const EntityTypeEnum = {
  INTERNAL_SOURCE: 1,
  OUT_SOURCE: 2,
  OPTIMIZED: 3,
  AUTO_OPTIMIZING: 4,
  AUTO_OPTIMIZED: 5,
  REVISED: 6,
  TREND_ARTICLE: 7,
  PLANNED_CONTENT: 8,
}

const EntityStatusEnum = {
  PUBLISHED: 1,
  DRAFT: 2,
}

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const assetStore = useAssetStore()
const contentStore = useContentStore()

// State
const isInitialLoading = ref(true)
const isLoading = ref(false)
const myData = ref([])
const currentPage = ref(contentStore.currentPage)
const pageCount = ref(contentStore.pageCount)
const totalCount = ref(contentStore.totalCount)
const itemsPerPage = ref(15)
const sortKey = ref(contentStore.sortKey)
const sortOrder = ref(contentStore.sortOrder)
const titleTooltipVisible = ref(false)
const searchQuery = ref(contentStore.searchQuery)
// Default olarak "Hepsi" filtresi seçili başlasın (ID: 1)
const selectedFilter = ref(contentStore.selectedFilter || 1)

// Table Configuration
const tableColumns = computed(() => [
  { key: 'title', label: t('entity.tableHeaders.title'), sortable: false, width: 'w-5/12', align: 'left' },
  { key: 'score', label: t('entity.tableHeaders.score'), sortable: true, width: 'w-1/12', align: 'center' },
  { key: 'status', label: t('entity.tableHeaders.status'), sortable: false, width: 'w-1/12', align: 'center' },
  { key: 'publicationStatus', label: t('entity.tableHeaders.publicationStatus'), sortable: false, width: 'w-2/12', align: 'center' },
  { key: 'date', label: t('entity.tableHeaders.date'), sortable: true, width: 'w-2/12', align: 'center' },
  { key: 'actions', label: '#', sortable: false, width: 'w-1/12', align: 'center' },
])

// Dynamic filter items with i18n support
const filterItems = computed(() => [
  { id: 1, name: t('entity.filterItems.all'), default: true },
  { id: 2, name: t('entity.filterItems.optimized') },
  { id: 3, name: t('entity.filterItems.autoOptimized') },
  { id: 4, name: t('entity.filterItems.scheduled') },
  { id: 5, name: t('entity.filterItems.notOptimized') },
])

// Computed
const tableProps = computed(() => {
  // Dynamically create filter items here for reactivity
  const currentFilterItems = [
    { id: 1, name: t('entity.filterItems.all'), default: true },
    { id: 2, name: t('entity.filterItems.optimized') },
    { id: 3, name: t('entity.filterItems.autoOptimized') },
    { id: 4, name: t('entity.filterItems.scheduled') },
    { id: 5, name: t('entity.filterItems.notOptimized') },
  ]

  return {
    isLoading: isLoading.value,
    itemsPerPage: itemsPerPage.value,
    totalCount: totalCount.value,
    pageCount: pageCount.value,
    currentPage: currentPage.value,
    sortKey: sortKey.value,
    sortOrder: sortOrder.value,
    searchIcon: 'search',
    searchPlaceholder: t('entity.searchPlaceholder'),
    showSearch: true,
    showFilter: true,
    showSearchText: false,
    filterOptions: [{
      id: 'status',
      icon: 'filter_alt',
      iconClass: 'material-symbols-outlined text-gray-600 !text-[22px]',
      showText: false,
      items: currentFilterItems,
    }],
    minWidth: '80vh',
    minHeight: '53vh',
    maxHeight: '53vh',
  }
})

const getPageTitle = computed(() => t('socialMedia.components.posts.pageTitle.contentManagement'))

const getPublicationStatus = (status) => {
  const statuses = {
    [EntityStatusEnum.PUBLISHED]: t('entity.publicationStatus.published'),
    [EntityStatusEnum.DRAFT]: t('entity.publicationStatus.draft'),
  }
  return statuses[status] || t('entity.publicationStatus.unknown')
}

const getPublicationStatusIcon = (status) => {
  switch (status) {
    case EntityStatusEnum.PUBLISHED:
      return 'task_alt'
    case EntityStatusEnum.DRAFT:
      return 'edit_note'
    default:
      return 'help'
  }
}

const getPublicationStatusColor = (status) => {
  switch (status) {
    case EntityStatusEnum.PUBLISHED:
      return 'text-green-500'
    case EntityStatusEnum.DRAFT:
      return 'text-blue-500'
    default:
      return 'text-gray-500'
  }
}

const formatDate = (item) => {
  return item.entityTypeId !== EntityTypeEnum.OUT_SOURCE
      ? dateHelper.formatLocalized(item.addDate, 'medium', { type: 'datetime' })
      : t('entity.unknown')
}

const getEntityStatus = (entityTypeId) => {
  const statuses = {
    [EntityTypeEnum.INTERNAL_SOURCE]: 'entity.statuses.newContent',
    [EntityTypeEnum.OUT_SOURCE]: 'entity.statuses.existingContent',
    [EntityTypeEnum.OPTIMIZED]: 'entity.statuses.optimized',
    [EntityTypeEnum.AUTO_OPTIMIZING]: 'entity.statuses.autoOptimizing',
    [EntityTypeEnum.AUTO_OPTIMIZED]: 'entity.statuses.autoOptimized',
    [EntityTypeEnum.REVISED]: 'entity.statuses.revised',
    [EntityTypeEnum.TREND_ARTICLE]: 'entity.statuses.trendContent',
    [EntityTypeEnum.PLANNED_CONTENT]: 'entity.statuses.plannedContent',
  }
  return t(statuses[entityTypeId] || 'entity.statuses.unknown')
}

const fetchData = async (forceRefresh = false) => {
  try {
    isLoading.value = true

    // API isteğini yap
    const result = await contentStore.fetchContentsList(assetStore.assetId, {
      q: searchQuery.value,
      page: currentPage.value - 1,
      sort: sortKey.value,
      order: sortOrder.value,
      filter: selectedFilter.value,
      forceRefresh,
    })

    // Verileri güncelle
    myData.value = result.detail
    totalCount.value = result.totalCount
    pageCount.value = result.pageCount

  } catch (err) {
    responseHandlerService.handleError(err, t('entity.fetchError'))
  } finally {
    isLoading.value = false
    isInitialLoading.value = false
  }
}

const handleSearch = (query) => {
  searchQuery.value = query
  currentPage.value = 1
  contentStore.setSearchParams({ searchQuery: query, page: 1 })

  // URL'i güncelle
  contentStore.syncStateWithUrl(router)

  fetchData()
}

// Filtre değişikliklerini işler ve veri çeker
const handleFilter = (filters) => {
  // FilterMenu'den gelen objenin yapısı: { status: "1" }
  // status özelliği içindeki değeri alıp itemId olarak kullanıyoruz
  const itemId = filters.status ? parseInt(filters.status) : 1 // Default olarak 1 (Hepsi)

  // Seçili filtre zaten aynıysa, işlem yapma
  if (selectedFilter.value === itemId) return

  selectedFilter.value = itemId
  currentPage.value = 1
  contentStore.setSearchParams({ selectedFilter: itemId, page: 1 })

  // Normal veri çekme çağrısı
  fetchData()
}

const handleSort = ({ key, order }) => {
  sortKey.value = key
  sortOrder.value = order
  contentStore.setSearchParams({ sortKey: key, sortOrder: order })

  // URL'i güncelle
  contentStore.syncStateWithUrl(router)

  fetchData()
}

const handlePageChange = (page) => {
  currentPage.value = page
  contentStore.setSearchParams({ page })

  // URL'i güncelle
  contentStore.syncStateWithUrl(router)

  fetchData()
}

const handleRowClick = (item) => {
  // Sadece store'daki içerik ID'sini ayarla ve görünüm durumunu güncelle
  contentStore.setSelectedContentId(item.id)
  contentStore.lastActiveView = 'detail'
}

const handleContentUpdated = () => {
  contentStore.clearSelectedContent()
  fetchData()
}

const checkIfTextOverflows = (event) => {
  const element = event.target
  if (element.offsetWidth < element.scrollWidth)
    titleTooltipVisible.value = true
}

const handleNewContentClick = () => {
  router.push({ query: { tab: 'new-content' } })
}

// Sayfa yüklenirken parametreleri kontrol et
onMounted(() => {
  // URL parametrelerini temizle - sadece tab parametresini koru
  const queryParams = route.query
  const tab = queryParams.tab || 'contents'

  // Kaydedilmiş geçişlere eklenmemesi için replace kullan
  router.replace({ query: { tab } }).then(() => {
    // URL temizlendikten sonra state güncellemeleri
    contentStore.lastActiveView = 'list'

    // Seçili içerik varsa temizle
    if (contentStore.getSelectedContentId) {
      contentStore.clearSelectedContent()
    }

    // Sayfa yüklenirken sadece bir kez veri çek
    fetchData()
  })
})
</script>
